import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses?limit=200`);
      setBusinesses(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading businesses:", error);
      toast.error("Failed to load businesses");
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/businesses/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading businesses...</p>
      </div>
    );
  }

  return (
    <div data-testid="businesses-page">
      <div className="page-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <h1 className="page-title">Businesses</h1>
            <p className="page-description">
              All discovered business leads from scans
            </p>
          </div>
          <Button
            data-testid="export-csv-btn"
            onClick={exportToCSV}
            disabled={exporting || businesses.length === 0}
            className="btn btn-primary"
            style={{ alignSelf: 'flex-start' }}
          >
            {exporting ? "Exporting..." : "📥 Export to CSV"}
          </Button>
        </div>
      </div>

      <div className="content-card">
        {businesses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏢</div>
            <div className="empty-state-title">No businesses found</div>
            <div className="empty-state-description">
              Run a scan to discover new business leads
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="data-table" data-testid="businesses-table" style={{ minWidth: '800px' }}>
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>City</th>
                  <th>Formation Date</th>
                  <th>Priority</th>
                  <th>CRM Synced</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((business) => (
                  <tr key={business.id} data-testid={`business-row-${business.id}`}>
                    <td>
                      <strong style={{ fontSize: '13px' }}>{business.business_name}</strong>
                      <br />
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {business.domain_guess}
                      </span>
                    </td>
                    <td>
                      {business.email ? (
                        <>
                          <a href={`mailto:${business.email}`} style={{ color: '#2563eb', textDecoration: 'none', fontSize: '12px', wordBreak: 'break-all' }}>
                            {business.email}
                          </a>
                          <br />
                          <span style={{ fontSize: '10px', color: '#64748b' }}>
                            Confidence: {business.email_confidence}%
                          </span>
                        </>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>No email</span>
                      )}
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {business.contact_first_name ? (
                        `${business.contact_first_name} ${business.contact_last_name || ''}`
                      ) : (
                        business.registered_agent_name || 'N/A'
                      )}
                    </td>
                    <td style={{ fontSize: '13px' }}>{business.city || 'N/A'}</td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(business.formation_date)}</td>
                    <td>
                      <span className={`status-badge priority-${business.priority.toLowerCase()}`} style={{ fontSize: '10px', padding: '3px 8px' }}>
                        {business.priority}
                      </span>
                    </td>
                    <td>
                      {business.ghl_synced ? (
                        <span style={{ color: '#16a34a', fontWeight: '600' }}>✓ Yes</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>✗ No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Businesses;
